import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { Pencil } from "lucide-react";
import { z } from "zod";

import { TextField } from "components/AddVaccineForm/components";
import { CardWithTitle } from "components/helpers/CardWithTitle/CardWithTitle.jsx";
import { Button } from "components/ui/Button";
import { Dialog } from "components/ui/Dialog";
import { DialogFooter } from "components/ui/Dialog/DialogFooter";
import { useCurrentPatientId } from "features/patients/hooks/useCurrentPatientId";
import { usePatientSocial } from "features/patients/hooks/usePatientSocial";
import { useSavePatientSocial } from "features/patients/hooks/useSavePatientSocial";
import { onInvalidSubmit } from "utils/reportFormErrors";

import s from "./Social.module.css";

const formSchema = z.object({
  exSmokerYears: z.string().optional(),
  alcoholUnitsPerWeek: z.string().optional(),
  homeLife: z.string().optional(),
  occupation: z.string().optional(),
});

const FIELDS = [
  { name: "exSmokerYears", label: "Smoking (years)" },
  { name: "alcoholUnitsPerWeek", label: "Alcohol (units per week)" },
  { name: "homeLife", label: "Home" },
  { name: "occupation", label: "Occupation" },
];

const Social = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const patientId = useCurrentPatientId();
  const { data: social, refetch } = usePatientSocial(patientId);
  const saveSocial = useSavePatientSocial();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      exSmokerYears: "",
      alcoholUnitsPerWeek: "",
      homeLife: "",
      occupation: "",
    },
  });

  useEffect(() => {
    if (social) {
      form.reset({
        exSmokerYears: social.exSmokerYears?.toString() || "",
        alcoholUnitsPerWeek: social.alcoholUnitsPerWeek?.toString() || "",
        homeLife: social.homeLife || "",
        occupation: social.occupation || "",
      });
    }
  }, [social]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = (data) => {
    saveSocial.mutate(
      {
        id: social?.id || null,
        patientId: Number(patientId),
        ...data,
      },
      {
        onSuccess: () => {
          toast.success("Social data saved successfully");
          setIsModalOpen(false);
          refetch();
        },
        onError: (error) => {
          console.error("Failed to save social data:", error);
          toast.error("Failed to save social data. Please try again.");
        },
      }
    );
  };

  const getDisplayValue = (field) => {
    if (!social) return "-";
    const value = social[field.name];
    if (value === null || value === undefined || value === "") return "-";
    if (field.name === "exSmokerYears") {
      return Number(value) > 0 ? `${value} PY` : "Not Smoking";
    }
    return value;
  };

  return (
    <>
      <CardWithTitle
        title="Social"
        endIcon={Pencil}
        buttonTitle="Edit"
        className={s.editButton}
        showButton={false}
        onButtonClick={() => setIsModalOpen(true)}
      >
        <ul className={s.listContainer}>
          {FIELDS.map((field) => (
            <li key={field.name} className={s.listItemContainer}>
              <p className={s.listItemTitle}>{field.label}:</p>
              <p className={s.listItemDescr}>{getDisplayValue(field)}</p>
            </li>
          ))}
        </ul>
      </CardWithTitle>

      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Social"
        width={700}
      >
        <FormProvider {...form}>
          <form
            onSubmit={form.handleSubmit(handleSave, onInvalidSubmit("Social"))}
            className={s.formContainer}
          >
            <div className={s.formGrid}>
              <TextField
                name="exSmokerYears"
                label="Smoking (years)"
                placeholder="Enter number of years"
                type="number"
              />
              <TextField
                name="alcoholUnitsPerWeek"
                label="Alcohol (units per week)"
                placeholder="Example: 13"
                type="number"
              />
              <TextField
                name="homeLife"
                label="Home"
                placeholder="Example: Lives with son"
              />
              <TextField
                name="occupation"
                label="Occupation"
                placeholder="Example: Retired teacher"
              />
            </div>
          </form>
        </FormProvider>
        <DialogFooter>
          <Button
            variant="outline"
            size="large"
            onClick={() => setIsModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="large"
            onClick={form.handleSubmit(handleSave, onInvalidSubmit("Social"))}
            disabled={saveSocial.isPending}
          >
            {saveSocial.isPending ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </Dialog>
    </>
  );
};

export { Social };
